import type { ServiceCardModel } from '@/pages/services/components/ServiceCard';
import type { MhProduct } from '@/types/mh';
import {
  formatProductPriceAmount,
  formatProductPriceUnit,
  parseProductTags,
  resolveAssetUrl,
} from '@/utils/product-display';

const PLACEHOLDER_ICON = `${import.meta.env.BASE_URL}vite.svg`;

export type ProductCategoryGroup = {
  key: string;
  categoryId: number | null;
  title: string;
  sort: number;
  products: MhProduct[];
};

function productSortOrder(a: MhProduct, b: MhProduct): number {
  return Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0);
}

/** 按分类分组；分类按 categorySortOrder 升序，组内产品按 sortOrder 升序 */
export function groupProductsByCategory(products: MhProduct[]): ProductCategoryGroup[] {
  const map = new Map<string, ProductCategoryGroup>();

  for (const p of products) {
    const key = p.categoryId != null ? String(p.categoryId) : 'none';
    const title = p.categoryName?.trim() || '其他';
    const sort = p.categorySortOrder != null ? Number(p.categorySortOrder) : 9999;
    const categoryId = p.categoryId ?? null;

    if (!map.has(key)) {
      map.set(key, { key, categoryId, title, sort, products: [] });
    }
    map.get(key)!.products.push(p);
  }

  const groups = Array.from(map.values()).sort((a, b) => a.sort - b.sort);
  for (const g of groups) {
    g.products.sort(productSortOrder);
  }
  return groups;
}

/** 「全部」视图：按分类顺序展平 */
export function flattenGroupedProducts(groups: ProductCategoryGroup[]): MhProduct[] {
  return groups.flatMap((g) => g.products);
}

function formatRmbReferenceAmount(price: number | string): string | undefined {
  const n = Number(price);
  if (!Number.isFinite(n)) {
    return undefined;
  }

  const amount = n * 7;
  const text = Number.isInteger(amount)
    ? String(amount)
    : amount.toFixed(2).replace(/\.?0+$/, '');
  return `￥${text}`;
}

export function mhProductToCard(p: MhProduct): ServiceCardModel {
  const tags = parseProductTags(p.tags);
  return {
    slug: p.productCode,
    name: p.productName,
    provider: '官方团队',
    rating: 5,
    description: p.description?.trim() || '信息查询服务，提交参数后生成订单，待审核处理。',
    priceAmount: formatProductPriceAmount(p.price, p),
    priceUnit: formatProductPriceUnit(p),
    rmbAmount: formatRmbReferenceAmount(p.price),
    options: '详见产品参数说明',
    icon: resolveAssetUrl(p.iconUrl) || PLACEHOLDER_ICON,
    tag: tags[0],
    categoryName: p.categoryName?.trim() || '其他',
  };
}
